/**
 * DashboardSmsTab — SMS tab for Operations Dashboard.
 * Same filter control as My Candidates - Retention: CustomFilter + ChipsContainer.
 * Recruiter dropdown (Placement Recruiter), thread list, and iMessage-style chat UI.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  Typography,
  TextField,
  IconButton,
  Skeleton,
  Divider,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SearchIcon from '@mui/icons-material/Search';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import GetAppIcon from '@mui/icons-material/GetApp';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectUser } from 'oidc/user.selectors';
import { Concatenate } from 'utils/string/string';
import {
  getDashboardRecruiterLongCode,
  getDashboardInbox,
  getDashboardConversationLogs,
  publishDashboardSms,
  getDashboardSmsLogsFromCosmos,
  getDashboardSmsConversationFromCosmos,
} from 'app/services/TaskServices/ManagementDashboardService';
import type { IInboxQuery } from 'app/models/Notification/Notification';
import { makeStyles } from 'tss-react/mui';
import { format } from 'date-fns';
import {
  DashboardSmsRecruiterFilter,
  DashboardSmsRecruiterChips,
  defaultSmsFilterValues,
  HIDE_RECRUITER_FILTER_FOR_SMS,
} from './DashboardFilter';

/** Temporary hardcode for development — recruiter with long code +1980898836 (Amy Waters). Remove when recruiter API is wired. */
const HARDCODED_RECRUITER_ID = '32132112';
const HARDCODED_RECRUITER = { id: 32132112, name: 'Amy Waters', value: 32132112 } as SmsRecruiterOption;

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: 'calc(100vh - 150px)',
    minHeight: 320,
    backgroundColor: '#fff',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    flexShrink: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#fff',
  },
  chipsRow: {
    flexShrink: 0,
    padding: theme.spacing(0, 2, 1, 2),
    backgroundColor: '#fff',
  },
  main: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },
  threadList: {
    width: 340,
    minWidth: 260,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f0f2f5',
  },
  threadSearch: {
    padding: theme.spacing(1.5, 2),
    flexShrink: 0,
    backgroundColor: '#f0f2f5',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: 8,
      fontSize: 14,
    },
  },
  threadListScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  threadItem: {
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid rgba(0,0,0,0.06)`,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
    '&.selected': { backgroundColor: '#e9edef' },
  },
  threadAvatar: {
    width: 48,
    height: 48,
    fontSize: 18,
    fontWeight: 600,
    backgroundColor: '#007AFF',
    color: '#fff',
  },
  chatArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    backgroundColor: '#efeae2',
  },
  chatHeader: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#f0f2f5',
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  chatHeaderAvatar: {
    width: 40,
    height: 40,
    fontSize: 16,
    fontWeight: 600,
    backgroundColor: '#007AFF',
    color: '#fff',
    flexShrink: 0,
  },
  chatHeaderInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  chatHeaderActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  dateSeparator: {
    alignSelf: 'center',
    margin: theme.spacing(1.5, 0),
    padding: theme.spacing(0.5, 2),
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.9)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    fontSize: 12,
    fontWeight: 500,
    color: 'rgba(0,0,0,0.6)',
  },
  chatMessages: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: theme.spacing(2, 2, 1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  chatInputArea: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#f0f2f5',
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  unreadBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#25d366',
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  messageBubble: {
    maxWidth: '75%',
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 20,
    alignSelf: 'flex-start',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  messageBubbleOutbound: {
    alignSelf: 'flex-end',
    backgroundColor: '#d9fdd3',
    color: 'rgba(0,0,0,0.87)',
    borderBottomRightRadius: 6,
    marginLeft: 'auto',
    boxShadow: '0 1px 0.5px rgba(0,0,0,0.08)',
  },
  messageBubbleInbound: {
    alignSelf: 'flex-start',
    backgroundColor: '#fff',
    color: 'rgba(0,0,0,0.87)',
    borderBottomLeftRadius: 6,
    boxShadow: '0 1px 0.5px rgba(0,0,0,0.08)',
  },
  senderPrefix: {
    fontSize: 11,
    fontWeight: 600,
    color: '#00a884',
    marginBottom: 4,
  },
  senderPrefixOutbound: {
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(0,0,0,0.7)',
    marginBottom: 4,
  },
  messageTime: {
    fontSize: 11,
    opacity: 0.7,
    marginTop: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  attachmentPreview: {
    position: 'relative',
    display: 'inline-block',
    marginTop: 4,
  },
  attachmentsWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  attachmentImage: {
    maxWidth: 220,
    maxHeight: 220,
    minHeight: 60,
    borderRadius: 8,
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: 'auto',
      maxHeight: 220,
      objectFit: 'cover',
      display: 'block',
      borderRadius: 8,
    },
  },
  attachmentFile: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.06)',
    maxWidth: 220,
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
  },
  attachmentFileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.05)',
    marginTop: 4,
    maxWidth: 220,
  },
  attachmentDownloadBtn: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    padding: 4,
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
  },
  sendButton: {
    backgroundColor: '#007AFF',
    color: '#fff',
    '&:hover': { backgroundColor: '#0056b3' },
    '&:disabled': { backgroundColor: '#c7c7cc', color: '#fff' },
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: 20,
      '& fieldset': { borderColor: '#c7c7cc' },
    },
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
  },
}));

export interface SmsRecruiterOption {
  id: number | string;
  userId?: number;
  employeeId?: number;
  value?: number;
  firstName?: string;
  lastName?: string;
  name?: string;
}

export interface DashboardSmsTabProps {
  smsRecruiters?: SmsRecruiterOption[];
  loading?: boolean;
}

function getRecruiterId(r: SmsRecruiterOption): string {
  return String((r as any).userId ?? (r as any).employeeId ?? (r as any).value ?? r.id);
}

/** Get initials from name or phone. For "Amy Waters" -> "AW". For "Name (phone)" skip parenthesized parts. */
function getInitials(name: string): string {
  const s = String(name ?? '').trim();
  if (!s || s === 'Unknown') return '';
  const parts = s.split(/\s+/).filter(p => p && !/^\(.*\)$/.test(p));
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts.length === 1 && /^[A-Za-z]/.test(parts[0])) return parts[0].slice(0, 2).toUpperCase();
  return '';
}

/** True when we should show default user icon (no meaningful initials). */
function useDefaultUserIcon(initials: string): boolean {
  return !initials || initials === '?' || /^\+?\d/.test(initials) || !/^[A-Za-z]{2}$/.test(initials);
}

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i;

/** Get attachment URLs from log.message.attachmentUrls or message.attachments[].contentUrl.
 * Handles API casing (message vs Message), single string vs array, wrapped log (item.log), and attachments[].contentUrl. */
function getAttachmentUrls(log: any): string[] {
  const entry = log?.log ?? log;
  const msg = entry?.message ?? entry?.Message;
  if (!msg) return [];
  const rawUrls = msg.attachmentUrls ?? msg.AttachmentUrls;
  const urlList = Array.isArray(rawUrls)
    ? rawUrls
    : typeof rawUrls === 'string' && rawUrls
    ? [rawUrls]
    : [];
  const attachList = msg.attachments ?? msg.Attachments ?? [];
  const fromAttachments = (Array.isArray(attachList) ? attachList : []).map((a: any) => {
    const signed = a?.signedUrl ?? a?.SignedUrl;
    const content = a?.contentUrl ?? a?.ContentUrl;
    const url = (typeof signed === 'string' ? signed : null) ?? (typeof content === 'string' ? content : null) ?? (typeof a?.url === 'string' ? a.url : null) ?? (typeof a?.Url === 'string' ? a.Url : null);
    return url;
  }).filter(Boolean);
  const combined = [...urlList, ...fromAttachments];
  return [...new Set(combined)].filter((u): u is string => typeof u === 'string' && u.length > 0);
}

function isImageUrl(url: string): boolean {
  return IMAGE_EXT.test(url?.split('?')[0] ?? '');
}

/** Renders image attachment with fallback when load fails (e.g. CORS). */
function AttachmentImage({ url, classes }: { url: string; classes: any }) {
  const [loadFailed, setLoadFailed] = useState(false);
  if (loadFailed) {
    return (
      <Box className={classes.attachmentFile} component="a" href={url} target="_blank" rel="noopener noreferrer">
        <InsertDriveFileIcon sx={{ fontSize: 20 }} />
        <span>View image</span>
        <GetAppIcon sx={{ fontSize: 16 }} />
      </Box>
    );
  }
  return (
    <Box className={classes.attachmentImage} component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>
      <img src={url} alt="" onError={() => setLoadFailed(true)} />
    </Box>
  );
}

/** Detect outbound: we (searched person) sent it → right/green. Received → left/white.
 * Use sender/tos match against searched phone; ignore Cosmos direction property.
 * - message.sender.phoneNumber === ourPhone → we sent → outbound (right)
 * - message.tos[].phoneNumber === ourPhone → we received → inbound (left) */
function isOutboundMessage(log: any, ourPhone?: string): boolean {
  if (!ourPhone) {
    const dir = (log?.message?.direction ?? '').toString().toLowerCase();
    if (dir === 'outbound' || dir === '1') return true;
    if (dir === 'inbound' || dir === '0') return false;
    const sendType = (log?.sendType ?? '').toString();
    if (sendType === 'Outbound') return true;
    if (sendType === 'Inbound') return false;
    return false;
  }
  const msg = log?.message ?? log?.Message;
  if (!msg) return false;
  const ourNorm = String(ourPhone).replace(/\D/g, '');
  const senderPhone = String(msg.sender?.phoneNumber ?? '').replace(/\D/g, '');
  // Sender matches our searched phone = we sent it = outbound = right
  if (senderPhone && ourNorm && senderPhone === ourNorm) return true;
  // Tos contains our phone = message sent TO us = we received = inbound = left
  const tos = msg.tos ?? [];
  const tosHasOurPhone = Array.isArray(tos) && tos.some((t: any) => {
    const p = String(t?.phoneNumber ?? '').replace(/\D/g, '');
    return p && ourNorm && p === ourNorm;
  });
  if (tosHasOurPhone) return false;
  // Fallback when neither matches (e.g. inbox path without phone search)
  const dir = (msg.direction ?? '').toString().toLowerCase();
  if (dir === 'outbound' || dir === '1') return true;
  if (dir === 'inbound' || dir === '0') return false;
  const sendType = (log?.sendType ?? '').toString();
  if (sendType === 'Outbound') return true;
  if (sendType === 'Inbound') return false;
  return false;
}

export const DashboardSmsTab: React.FC<DashboardSmsTabProps> = ({ smsRecruiters = [], loading }) => {
  const { classes } = useStyles();
  const userInfo = useSelector(selectUser)?.userInfo;
  const formMethods = useForm({ defaultValues: defaultSmsFilterValues, shouldUnregister: false });
  const { setValue, handleSubmit, watch } = formMethods;
  const watchedSenderPhone = watch('senderPhoneNumber');

  const [selectedRecruiterId, setSelectedRecruiterId] = useState<string>('');
  const [senderPhoneNumber, setSenderPhoneNumber] = useState<string>('');
  const [cosmosLogs, setCosmosLogs] = useState<any>(null);
  const [cosmosLogsLoading, setCosmosLogsLoading] = useState(false);
  const [longCode, setLongCode] = useState<{ number?: string } | null>(null);
  const [inbox, setInbox] = useState<any>(null);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [selectedThread, setSelectedThread] = useState<any>(null);
  const [conversation, setConversation] = useState<any>(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [threadSearchQuery, setThreadSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversation?.results, scrollToBottom]);

  // Auto-select current user (first in list) when recruiter options load — same as My Candidates - Retention
  useEffect(() => {
    if (userInfo?.employeeId && smsRecruiters.length > 0) {
      const currentUser = smsRecruiters.find((r: any) => (r.id ?? r.value) === userInfo.employeeId);
      if (currentUser) {
        setValue('placementRecruiter', currentUser);
        setSelectedRecruiterId(getRecruiterId(currentUser));
      }
    }
  }, [userInfo?.employeeId, smsRecruiters.length, setValue]);

  // Temporary: when recruiter options are empty (API fail), use hardcoded recruiter 32132112 to proceed with data
  useEffect(() => {
    if (!loading && smsRecruiters.length === 0 && !selectedRecruiterId && !senderPhoneNumber) {
      setValue('placementRecruiter', HARDCODED_RECRUITER);
      setSelectedRecruiterId(HARDCODED_RECRUITER_ID);
    }
  }, [loading, smsRecruiters.length, selectedRecruiterId, senderPhoneNumber, setValue]);

  const onApplyRecruiter = useCallback(
    (values: Record<string, unknown>) => {
      const recruiter = values.placementRecruiter as SmsRecruiterOption | null;
      const phone = String(values.senderPhoneNumber ?? '').trim();
      if (phone) {
        setSenderPhoneNumber(phone);
        setSelectedRecruiterId('');
        setValue('placementRecruiter', null);
      } else if (recruiter) {
        setSelectedRecruiterId(getRecruiterId(recruiter));
        setSenderPhoneNumber('');
        setValue('senderPhoneNumber', '');
      } else {
        setSelectedRecruiterId('');
        setSenderPhoneNumber('');
      }
    },
    [setValue]
  );

  const resetFilter = useCallback(() => {
    Object.entries(defaultSmsFilterValues).forEach(([key, val]) => setValue(key, val));
    setSelectedRecruiterId('');
    setSenderPhoneNumber('');
    setCosmosLogs(null);
    onApplyRecruiter(defaultSmsFilterValues);
  }, [setValue, onApplyRecruiter]);

  // Fetch Cosmos logs when phone is set — automatically search partitionKey, senderPhone, and tosPhone; merge and dedupe
  useEffect(() => {
    if (!senderPhoneNumber) {
      setCosmosLogs(null);
      setSelectedThread(null);
      setConversation(null);
      setLongCode(null);
      return;
    }
    setCosmosLogsLoading(true);
    setSelectedThread(null);
    setConversation(null);
    setLongCode({ number: senderPhoneNumber });
    const opts = { senderPhoneNumber, pageNumber: 1, pageSize: 50 };
    Promise.all([
      getDashboardSmsLogsFromCosmos({ ...opts, searchType: 'partitionKey' }),
      getDashboardSmsLogsFromCosmos({ ...opts, searchType: 'senderPhone' }),
      getDashboardSmsLogsFromCosmos({ ...opts, searchType: 'tosPhone' }),
    ])
      .then(([pk, sp, tp]) => {
        const seen = new Set<string>();
        const merged: any[] = [];
        const add = (item: any) => {
          const key = item?.conversationId ?? item?.latestLog?.id ?? '';
          if (key && !seen.has(key)) {
            seen.add(key);
            merged.push(item);
          }
        };
        (pk?.results ?? []).forEach(add);
        (sp?.results ?? []).forEach(add);
        (tp?.results ?? []).forEach(add);
        merged.sort((a, b) => {
          const ta = a?.latestLog?.publishedOn ?? 0;
          const tb = b?.latestLog?.publishedOn ?? 0;
          return new Date(tb).getTime() - new Date(ta).getTime();
        });
        setCosmosLogs({ results: merged, pageNumber: 1, pageSize: 50, totalCount: merged.length });
      })
      .catch(() => setCosmosLogs(null))
      .finally(() => setCosmosLogsLoading(false));
  }, [senderPhoneNumber]);

  // Fetch long code when recruiter changes (only when using recruiter path; phone search uses Cosmos effect for longCode)
  useEffect(() => {
    if (!selectedRecruiterId) {
      if (senderPhoneNumber) return; // Phone search: longCode is set by Cosmos effect, don't clear
      setLongCode(null);
      setInbox(null);
      setSelectedThread(null);
      setConversation(null);
      return;
    }
    setInbox(null);
    setSelectedThread(null);
    setConversation(null);
    getDashboardRecruiterLongCode(selectedRecruiterId)
      .then(res => setLongCode(res ?? null))
      .catch(() => setLongCode(null));
  }, [selectedRecruiterId, senderPhoneNumber]);

  // Fetch inbox when long code is available
  useEffect(() => {
    if (!selectedRecruiterId || !longCode?.number) {
      setInbox(null);
      return;
    }
    setInboxLoading(true);
    const inboxQuery: IInboxQuery = {
      userId: selectedRecruiterId,
      phoneNumber: longCode.number,
      email: '',
      pageSize: 50,
      pageNumber: 1,
    };
    getDashboardInbox(inboxQuery)
      .then(res => setInbox(res ?? null))
      .catch(() => setInbox(null))
      .finally(() => setInboxLoading(false));
  }, [selectedRecruiterId, longCode?.number]);

  // Fetch conversation when thread selected
  useEffect(() => {
    if (!selectedThread || !longCode?.number) {
      setConversation(null);
      return;
    }
    if (senderPhoneNumber) {
      // Cosmos path: fetch by conversationId + partitionKey (sender phone)
      const convId = selectedThread?.conversationId ?? selectedThread?.latestLog?.conversationId ?? selectedThread?.latestLog?.id ?? '';
      if (!convId) {
        setConversation(null);
        return;
      }
      setConversationLoading(true);
      getDashboardSmsConversationFromCosmos({
        conversationId: convId,
        senderPhoneNumber,
        pageNumber: 1,
        pageSize: 100,
      })
        .then(res => {
          const logs = res?.results ?? res?.Results ?? [];
          if (logs.length === 0 && selectedThread?.latestLog) {
            setConversation({ results: [selectedThread.latestLog] });
          } else {
            setConversation(logs.length ? { results: logs } : null);
          }
        })
        .catch(() => {
          if (selectedThread?.latestLog) {
            setConversation({ results: [selectedThread.latestLog] });
          } else {
            setConversation(null);
          }
        })
        .finally(() => setConversationLoading(false));
      return;
    }
    // Inbox path: fetch by contactId + phoneNumber + initiatedBy
    const contactId = selectedThread?.log?.message?.tos?.[0]?.contactId ?? selectedThread?.travelerId ?? '';
    const phoneNumber = selectedThread?.log?.message?.tos?.[0]?.phoneNumber ?? selectedThread?.log?.message?.sender?.phoneNumber ?? selectedThread?.contactPhone ?? '';
    if (!contactId && !phoneNumber) {
      setConversation(null);
      return;
    }
    setConversationLoading(true);
    getDashboardConversationLogs({
      contactId: String(contactId),
      phoneNumber: phoneNumber || undefined,
      pageSize: 50,
      pageNumber: 1,
      channel: ['SMS'],
      initiatedBy: longCode.number,
    })
      .then(res => setConversation(res ?? null))
      .catch(() => setConversation(null))
      .finally(() => setConversationLoading(false));
  }, [selectedThread, longCode?.number, senderPhoneNumber]);

  const handleSendMessage = useCallback(async () => {
    const text = messageText?.trim();
    if (!text || !selectedThread || !longCode?.number || sending) return;
    const tos = selectedThread?.log?.message?.tos?.length
      ? [{ phoneNumber: selectedThread.log.message.tos[0].phoneNumber, contactId: selectedThread.log.message.tos[0].contactId ?? selectedThread.travelerId }]
      : selectedThread?.contactPhone
        ? [{ phoneNumber: selectedThread.contactPhone, contactId: selectedThread.travelerId }]
        : [];
    const payload = {
      body: text,
      tos: tos.length ? tos : [{ phoneNumber: selectedThread?.contactPhone ?? selectedThread?.log?.message?.tos?.[0]?.phoneNumber }],
      sender: { phoneNumber: longCode.number },
      channel: 'SMS',
      sendType: 'OneToOne',
      useType: 'Candidate',
    };
    setSending(true);
    try {
      await publishDashboardSms(payload);
      setMessageText('');
      if (senderPhoneNumber) {
        const convId = selectedThread?.conversationId ?? selectedThread?.latestLog?.conversationId ?? selectedThread?.latestLog?.id ?? '';
        if (convId) {
          const refreshed = await getDashboardSmsConversationFromCosmos({
            conversationId: convId,
            senderPhoneNumber,
            pageNumber: 1,
            pageSize: 100,
          });
          setConversation(refreshed ?? conversation);
        }
      } else {
        const refreshed = await getDashboardConversationLogs({
          contactId: String(selectedThread?.log?.message?.tos?.[0]?.contactId ?? selectedThread?.travelerId ?? ''),
          phoneNumber: selectedThread?.contactPhone ?? selectedThread?.log?.message?.tos?.[0]?.phoneNumber,
          pageSize: 50,
          pageNumber: 1,
          channel: ['SMS'],
          initiatedBy: longCode.number,
        });
        setConversation(refreshed ?? conversation);
      }
    } finally {
      setSending(false);
    }
  }, [messageText, selectedThread, longCode, sending, conversation, senderPhoneNumber]);

  const inboxResults = inbox?.contacts?.results ?? inbox?.results ?? [];
  const cosmosResults = cosmosLogs?.results ?? [];
  const useCosmosPath = !!senderPhoneNumber;
  const threadListItems = useCosmosPath ? cosmosResults : inboxResults;
  const threadListLoading = useCosmosPath ? cosmosLogsLoading : inboxLoading;
  const threadName =
    selectedThread?.name ??
    selectedThread?.contactName ??
    (selectedThread?.senderName ? `${selectedThread.senderName} (${selectedThread.senderPhone ?? ''})` : null) ??
    selectedThread?.log?.message?.tos?.[0]?.phoneNumber ??
    selectedThread?.contactPhone ??
    selectedThread?.senderPhone ??
    'Unknown';

  /** Collect all phone numbers and names for header (e.g. "Name, +91 xxx, +91 yyy") */
  const headerPhones: string[] = [];
  if (selectedThread) {
    const add = (v: string) => v && !headerPhones.includes(v) && headerPhones.push(v);
    add(selectedThread.contactName ?? selectedThread.senderName);
    add(selectedThread.contactPhone ?? selectedThread.senderPhone);
    const tos = selectedThread?.log?.message?.tos ?? selectedThread?.latestLog?.message?.tos ?? [];
    tos.forEach((t: any) => {
      add(t?.phoneNumber);
      if (t?.name && t.name !== t.phoneNumber) add(t.name);
    });
  }
  const headerSubtitle = headerPhones.filter(Boolean).join(', ');

  /** Display name and phone for header (split for layout). */
  const headerDisplayName =
    selectedThread?.contactName ?? selectedThread?.senderName ?? selectedThread?.name ?? '';
  const headerDisplayPhone =
    selectedThread?.contactPhone ??
    selectedThread?.senderPhone ??
    selectedThread?.log?.message?.tos?.[0]?.phoneNumber ??
    selectedThread?.latestLog?.message?.tos?.[0]?.phoneNumber ??
    selectedThread?.latestLog?.message?.sender?.phoneNumber ??
    '';
  const headerDisplayTitle =
    headerDisplayName && headerDisplayPhone && headerDisplayName !== headerDisplayPhone
      ? `${headerDisplayName} (${headerDisplayPhone})`
      : headerDisplayPhone || headerDisplayName || 'Unknown';

  /** Last message date/time: from conversation (most recent) or selectedThread latestLog. */
  const lastMessageDate = (() => {
    const convLogs = (conversation?.results ?? []).map((r: any) => r?.log ?? r);
    if (convLogs.length > 0) {
      const latest = convLogs.reduce((a: any, b: any) =>
        new Date(a?.publishedOn || 0) >= new Date(b?.publishedOn || 0) ? a : b,
      );
      return latest?.publishedOn;
    }
    const ts = selectedThread?.latestLog?.publishedOn ?? selectedThread?.log?.publishedOn;
    return ts || null;
  })();
  const lastMessageDateStr = lastMessageDate
    ? format(new Date(lastMessageDate), 'dd MMM yyyy, h:mm a')
    : '';

  /** Group messages by date for separators */
  function groupMessagesByDate(logs: any[]) {
    const groups: { date: string; logs: any[] }[] = [];
    let currentDate = '';
    logs.forEach(log => {
      const d = log?.publishedOn ? format(new Date(log.publishedOn), 'd MMMM yyyy') : '';
      if (d && d !== currentDate) {
        currentDate = d;
        groups.push({ date: currentDate, logs: [] });
      }
      if (groups.length) groups[groups.length - 1].logs.push(log);
      else groups.push({ date: d, logs: [log] });
    });
    return groups;
  }

  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.filterRow}>
          <Skeleton width={220} height={40} />
        </Box>
        <Box className={classes.main}>
          <Skeleton variant="rectangular" width={280} height="100%" />
          <Box className={classes.emptyState} sx={{ flex: 1 }}>
            <Typography>Loading SMS...</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onApplyRecruiter)}>
        <Box className={classes.root}>
          <Box className={classes.filterRow}>
            <DashboardSmsRecruiterFilter
              recruiterOptions={smsRecruiters}
              onApplyFilter={onApplyRecruiter}
              onAfterReset={resetFilter}
            />
          </Box>
          <Box className={classes.chipsRow}>
            <DashboardSmsRecruiterChips onApplyFilter={onApplyRecruiter} />
          </Box>

          <Box className={classes.main}>
            <Box className={classes.threadList}>
              {!selectedRecruiterId && !senderPhoneNumber ? (
                <Box className={classes.emptyState} sx={{ p: 2 }}>
                  <Typography variant="body2">Enter sender phone (partition key) to view threads</Typography>
                </Box>
              ) : senderPhoneNumber ? (
                cosmosLogsLoading ? (
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="rectangular" height={56} sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" height={56} sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" height={56} />
                  </Box>
                ) : !(cosmosLogs?.results?.length) ? (
                  <Box className={classes.emptyState} sx={{ p: 2 }}>
                    <Typography variant="body2">No SMS threads (Cosmos)</Typography>
                  </Box>
                ) : (
                  <>
                    <Box className={classes.threadSearch}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search or start new chat"
                        value={threadSearchQuery}
                        onChange={e => setThreadSearchQuery(e.target.value)}
                        InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} /> }}
                      />
                    </Box>
                    <Box className={classes.threadListScroll}>
                      <List dense disablePadding>
                        {(cosmosLogs?.results ?? [])
                          .filter((item: any) => {
                            if (!threadSearchQuery.trim()) return true;
                            const q = threadSearchQuery.toLowerCase();
                            const phone = (item.contactPhone ?? item.senderPhone ?? item.latestLog?.message?.tos?.[0]?.phoneNumber ?? item.latestLog?.message?.sender?.phoneNumber ?? '').toString().toLowerCase();
                            const name = (item.contactName ?? item.senderName ?? '').toString().toLowerCase();
                            return name.includes(q) || phone.includes(q);
                          })
                          .map((item: any, idx: number) => {
                            const name = item.contactName ?? item.senderName ?? '';
                            const phone =
                              item.contactPhone ??
                              item.senderPhone ??
                              item.latestLog?.message?.tos?.[0]?.phoneNumber ??
                              item.latestLog?.message?.sender?.phoneNumber ??
                              '';
                            const displayTitle =
                              name && phone && name !== phone
                                ? `${name} (${phone})`
                                : phone || name || 'Unknown';
                            const preview = item.latestLog?.message?.body ?? '';
                            const ts = item.latestLog?.publishedOn;
                            const timeStr = ts
                              ? (() => {
                                  const d = new Date(ts);
                                  const now = new Date();
                                  if (d.toDateString() === now.toDateString()) return format(d, 'HH:mm');
                                  if (d.getTime() > now.getTime() - 86400000 * 2) return 'Yesterday';
                                  return format(d, 'dd/MM/yy');
                                })()
                              : '';
                            const unreadCount = item.unreadCount ?? 0;
                            return (
                              <ListItem key={item.conversationId ?? idx} disablePadding>
                                <ListItemButton
                                  className={`${classes.threadItem} ${selectedThread === item ? 'selected' : ''}`}
                                  selected={selectedThread === item}
                                  onClick={() => setSelectedThread(item)}
                                >
                                  <ListItemAvatar>
                                    <Avatar className={classes.threadAvatar}>
                                      {useDefaultUserIcon(getInitials(displayTitle)) ? (
                                        <PersonIcon sx={{ fontSize: 24 }} />
                                      ) : (
                                        getInitials(displayTitle)
                                      )}
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" fontWeight={unreadCount ? 600 : 400} noWrap>
                                          {displayTitle}
                                        </Typography>
                                        {timeStr && (
                                          <Typography variant="caption" color="text.secondary">
                                            {timeStr}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                    secondary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.25 }}>
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                                          {preview ? (preview.length > 35 ? preview.slice(0, 35) + '…' : preview) : ''}
                                        </Typography>
                                        {unreadCount > 0 && (
                                          <Box className={classes.unreadBadge}>{unreadCount > 99 ? '99+' : unreadCount}</Box>
                                        )}
                                      </Box>
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                      </List>
                    </Box>
                  </>
                )
              ) : inboxLoading ? (
                <Box sx={{ p: 2 }}>
                  <Skeleton variant="rectangular" height={56} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={56} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={56} />
                </Box>
              ) : inboxResults.length === 0 ? (
                <Box className={classes.emptyState} sx={{ p: 2 }}>
                  <Typography variant="body2">No SMS threads</Typography>
                </Box>
              ) : (
                <>
                  <Box className={classes.threadSearch}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search or start new chat"
                      value={threadSearchQuery}
                      onChange={e => setThreadSearchQuery(e.target.value)}
                      InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} /> }}
                    />
                  </Box>
                  <Box className={classes.threadListScroll}>
                    <List dense disablePadding>
                      {inboxResults
                        .filter((item: any) => {
                          if (!threadSearchQuery.trim()) return true;
                          const q = threadSearchQuery.toLowerCase();
                          const name = (item.name ?? item.log?.message?.tos?.[0]?.phoneNumber ?? '').toString().toLowerCase();
                          const phone = (item.log?.message?.tos?.[0]?.phoneNumber ?? '').toString().toLowerCase();
                          return name.includes(q) || phone.includes(q);
                        })
                        .map((item: any, idx: number) => {
                          const name = (item.name ?? '').toString().trim() || null;
                          const phone = item.log?.message?.tos?.[0]?.phoneNumber ?? item.log?.message?.sender?.phoneNumber ?? '';
                          const displayTitle =
                            name && phone && name !== phone
                              ? `${name} (${phone})`
                              : phone || name || 'Unknown';
                          const preview = item.log?.message?.body ?? '';
                          const unreadCount = item.unreadLogs ?? 0;
                          const ts = item.log?.publishedOn;
                          const timeStr = ts
                            ? (() => {
                                const d = new Date(ts);
                                const now = new Date();
                                if (d.toDateString() === now.toDateString()) return format(d, 'HH:mm');
                                if (d.getTime() > now.getTime() - 86400000 * 2) return 'Yesterday';
                                return format(d, 'dd/MM/yy');
                              })()
                            : '';
                          return (
                            <ListItem key={item.log?.id ?? idx} disablePadding>
                              <ListItemButton
                                className={`${classes.threadItem} ${selectedThread === item ? 'selected' : ''}`}
                                selected={selectedThread === item}
                                onClick={() => setSelectedThread(item)}
                              >
                                <ListItemAvatar>
                                  <Avatar className={classes.threadAvatar}>
                                    {useDefaultUserIcon(getInitials(displayTitle)) ? (
                                      <PersonIcon sx={{ fontSize: 24 }} />
                                    ) : (
                                      getInitials(displayTitle)
                                    )}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" fontWeight={unreadCount ? 600 : 400} noWrap>
                                        {displayTitle}
                                      </Typography>
                                      {timeStr && (
                                        <Typography variant="caption" color="text.secondary">
                                          {timeStr}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.25 }}>
                                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                                        {preview ? (preview.length > 35 ? preview.slice(0, 35) + '…' : preview) : ''}
                                      </Typography>
                                      {unreadCount > 0 && (
                                        <Box className={classes.unreadBadge}>{unreadCount > 99 ? '99+' : unreadCount}</Box>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                    </List>
                  </Box>
                </>
              )}
            </Box>

            <Box className={classes.chatArea}>
              {!selectedThread ? (
                <Box className={classes.emptyState}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                    <Typography>Select a thread to view conversation</Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box className={classes.chatHeader}>
                    <Avatar className={classes.chatHeaderAvatar}>
                      {useDefaultUserIcon(getInitials(headerDisplayTitle)) ? (
                        <PersonIcon sx={{ fontSize: 22 }} />
                      ) : (
                        getInitials(headerDisplayTitle)
                      )}
                    </Avatar>
                    <Box className={classes.chatHeaderInfo}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {headerDisplayName || headerDisplayPhone || 'Unknown'}
                      </Typography>
                      {headerDisplayName && headerDisplayPhone && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }} noWrap>
                          {headerDisplayPhone}
                        </Typography>
                      )}
                      {lastMessageDateStr && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }} noWrap>
                          Last message: {lastMessageDateStr}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box className={classes.chatMessages}>
                    {conversationLoading ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Skeleton variant="rounded" width="60%" height={48} />
                        <Skeleton variant="rounded" width="70%" height={48} sx={{ alignSelf: 'flex-end' }} />
                        <Skeleton variant="rounded" width="50%" height={48} />
                      </Box>
                    ) : (
                      <>
                        {groupMessagesByDate(
                          (conversation?.results ?? []).map((item: any) => item?.log ?? item)
                        ).map(({ date, logs }, groupIdx) => (
                          <React.Fragment key={date || `group-${groupIdx}`}>
                            {date && (
                              <Typography className={classes.dateSeparator}>
                                {date.toUpperCase()}
                              </Typography>
                            )}
                            {logs.map((item: any, idx: number) => {
                              const log = item?.log ?? item;
                              const ourPhone = senderPhoneNumber || longCode?.number;
                              const isOutbound = isOutboundMessage(log, ourPhone);
                              const body = log?.message?.body ?? log?.Message?.body ?? '';
                              const attachmentUrls = getAttachmentUrls(log);
                              const pubDate = log?.publishedOn ? format(new Date(log.publishedOn), 'h:mm a') : '';
                              const senderPhone = log?.senderPhone ?? log?.message?.sender?.phoneNumber ?? '';
                              const senderName = log?.senderName ?? log?.message?.sender?.name ?? '';
                              const senderPrefix =
                                senderPhone || senderName
                                  ? `${senderPhone || ''}${senderPhone && senderName ? ' - ' : ''}${senderName || ''}`
                                  : '';
                              return (
                                <Box
                                  key={log?.id ?? idx}
                                  className={`${classes.messageBubble} ${isOutbound ? classes.messageBubbleOutbound : classes.messageBubbleInbound}`}
                                >
                                  {senderPrefix && (
                                    <Typography className={isOutbound ? classes.senderPrefixOutbound : classes.senderPrefix}>
                                      {senderPrefix}
                                    </Typography>
                                  )}
                                  {/* 1. Message body first */}
                                  {body && <Typography variant="body2" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>{body}</Typography>}
                                  {/* 2. Attachments on new line within same bubble: images inline, files as icon + inline download */}
                                  {attachmentUrls.length > 0 && (
                                    <Box className={classes.attachmentsWrap} sx={{ mt: body ? 1 : 0 }}>
                                      {attachmentUrls.map((url: string, urlIdx: number) =>
                                        isImageUrl(url) ? (
                                          <AttachmentImage key={urlIdx} url={url} classes={classes} />
                                        ) : (
                                          <Box
                                            key={urlIdx}
                                            className={classes.attachmentFile}
                                            component="a"
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ display: 'inline-flex' }}
                                          >
                                            <InsertDriveFileIcon sx={{ fontSize: 20 }} />
                                            <span>Download</span>
                                            <GetAppIcon sx={{ fontSize: 16 }} />
                                          </Box>
                                        )
                                      )}
                                    </Box>
                                  )}
                                  <Typography className={classes.messageTime}>
                                    {pubDate}
                                    {isOutbound && (
                                      <DoneAllIcon sx={{ fontSize: 16, ml: 0.5 }} color="inherit" />
                                    )}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </React.Fragment>
                        ))}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </Box>
                  <Box className={classes.chatInputArea}>
                    <TextField
                      className={classes.inputField}
                      multiline
                      maxRows={4}
                      placeholder="Type a message"
                      value={messageText}
                      onChange={e => setMessageText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      size="small"
                      fullWidth
                      disabled={sending}
                    />
                    <IconButton className={classes.sendButton} onClick={handleSendMessage} disabled>
                      <SendIcon />
                    </IconButton>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
